"use client";

import { useScrollVisibility } from "@/hooks/ui";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import Link from "next/link";

/**
 * Two visual roles:
 * - Regular item: 56px tall, lives inline with the row, supports a two-line
 *   label (e.g. "Trade & Earn"), and supports a `variant="arena"` gold treatment.
 * - Highlighted item: the centerpiece CTA, overflows above the row (-mt-3) with
 *   a rounded-top tab and a glowing active state. Designed for one item per row.
 */
export interface BottomNavItem {
  label: string;
  icon: LucideIcon;
  href: string;
  isActive: boolean;
  isHighlighted?: boolean;
  /**
   * Visual variant for non-highlighted items. `arena` paints the icon and label
   * gold (medal-gold token) so the Cede Arena entry stands out from the standard
   * nav items. Ignored when `isHighlighted` is true.
   */
  variant?: "default" | "arena";
}

const splitLabel = (item: BottomNavItem): { primary: string; secondary: string } => {
  // Labels containing " & " (e.g. "Trade & Earn") get the two-line treatment.
  const ampersandIdx = item.label.indexOf(" & ");
  if (ampersandIdx >= 0) {
    return {
      primary: item.label.slice(0, ampersandIdx),
      secondary: item.label.slice(ampersandIdx + 3),
    };
  }
  return { primary: item.label, secondary: "" };
};

interface HighlightedItemContentProps {
  item: BottomNavItem;
}

const HighlightedItemContent = ({ item }: HighlightedItemContentProps) => {
  const Icon = item.icon;
  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center gap-0.5",
        "px-5 py-2 -mt-3 min-h-[64px]",
        "rounded-t-xl rounded-b-none",
        "transition-all duration-200 ease-out",
        "active:scale-[0.98]",
        // Active state: brighter background with glowing ring
        item.isActive
          ? "bg-cede-menu-highlight-active ring-2 ring-cede-primary/50 shadow-[0_0_12px_var(--color-cede-logo-shadow-blue,rgba(94,166,255,0.3))]"
          : "bg-cede-menu-highlight hover:brightness-125",
      )}
    >
      <span className="flex h-5 w-5 items-center justify-center">
        <Icon
          className={cn(
            "w-5 h-5 transition-all duration-200",
            item.isActive ? "text-cede-primary scale-110" : "text-cede-primary",
          )}
        />
      </span>
      <span
        className={cn(
          "text-[10px] font-medium leading-tight whitespace-nowrap transition-colors duration-200",
          item.isActive ? "text-cede-text-primary" : "text-cede-primary",
        )}
      >
        {item.label}
      </span>
    </div>
  );
};

interface RegularItemContentProps {
  item: BottomNavItem;
}

const RegularItemContent = ({ item }: RegularItemContentProps) => {
  const Icon = item.icon;
  const { primary, secondary } = splitLabel(item);
  const hasSecondary = secondary.length > 0;
  const isArena = item.variant === "arena";
  const { isActive } = item;

  // Color tokens — Arena variant uses medal-gold to mirror the sidebar treatment.
  const iconColor = isArena
    ? isActive
      ? "text-medal-gold"
      : "text-medal-gold/75"
    : isActive
      ? "text-cede-primary"
      : "text-cede-text-secondary";
  const labelColor = isArena
    ? isActive
      ? "text-medal-gold"
      : "text-medal-gold/85"
    : isActive
      ? "text-cede-primary"
      : "text-cede-text-secondary";
  const activePillColor = isArena ? "bg-medal-gold/15" : "bg-cede-bg-component";
  const secondaryLabelColor = isArena
    ? isActive
      ? "text-medal-gold/70"
      : "text-medal-gold/60"
    : isActive
      ? "text-cede-primary/70"
      : "text-cede-text-tertiary";

  return (
    <>
      {/* Active indicator pill background */}
      <span
        className={cn(
          "absolute inset-x-1 top-1 bottom-1 rounded-md transition-all duration-200",
          isActive ? activePillColor : "bg-transparent",
        )}
      />
      {/* Icon container */}
      <span
        className={cn(
          "relative flex h-4 w-4 items-center justify-center",
          "transition-transform duration-200",
          isActive && "scale-110",
        )}
      >
        <Icon className={cn("w-4 h-4 transition-colors duration-200", iconColor)} />
      </span>
      {/* Label */}
      <span className={cn("relative text-[10px] font-medium leading-tight transition-colors duration-200", labelColor)}>
        {primary}
      </span>
      {hasSecondary && (
        <span
          className={cn(
            "absolute bottom-1 left-1/2 -translate-x-1/2 text-[7px] font-normal leading-tight whitespace-nowrap",
            secondaryLabelColor,
          )}
        >
          & {secondary}
        </span>
      )}
    </>
  );
};

const HIGHLIGHTED_OUTER_CLASS = "flex flex-1 items-stretch justify-center";
const REGULAR_OUTER_CLASS = cn(
  "relative flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2",
  "min-h-[56px] select-none",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cede-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-cede-bg-drawer",
  "transition-all duration-200 ease-out",
  "active:scale-95",
);

const renderItem = (item: BottomNavItem) => (
  <Link
    key={item.label}
    aria-label={item.label}
    aria-current={item.isActive ? "page" : undefined}
    href={item.href}
    className={item.isHighlighted ? HIGHLIGHTED_OUTER_CLASS : REGULAR_OUTER_CLASS}
  >
    {item.isHighlighted ? <HighlightedItemContent item={item} /> : <RegularItemContent item={item} />}
  </Link>
);

/**
 * Global mobile bottom nav: glass-morphism footer chrome, safe-area inset,
 * 56px regular items, hide-on-scroll-down, and the highlighted center-CTA.
 */
export const BottomNav = ({ items }: { items: BottomNavItem[] }) => {
  const isVisible = useScrollVisibility();
  return (
    <footer
      className={cn(
        "fixed bottom-0 left-0 z-fixed w-screen",
        "pb-[env(safe-area-inset-bottom)]",
        "transition-transform duration-300 ease-out",
        !isVisible && "translate-y-full overflow-hidden",
      )}
    >
      {/* Glass morphism background */}
      <div className="absolute inset-0 bg-cede-bg-drawer/90 backdrop-blur-xl border-t border-cede-border-container" />

      {/* Content container */}
      <nav className="relative flex w-full items-end justify-between px-2">{items.map(renderItem)}</nav>
    </footer>
  );
};
